import React, { useMemo, useState } from 'react';

import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';

import { StepParamConfigById } from 'client/app/apps/protocols/context/StepsProvider/stepsConfig';
import { CreateGlobalConfigInputStepState } from 'client/app/apps/protocols/context/StepsProvider/stepState';
import { EntrySelector } from 'client/app/apps/protocols/EditProtocol/DefineProtocol/InstancePanel/EntrySelector';
import { PanelItem } from 'client/app/apps/protocols/EditProtocol/DefineProtocol/InstancePanel/PanelItem';
import { GLOBALMIXERCONFIG } from 'client/app/lib/workflow/workflowConfigProperties';
import { isDefined } from 'common/lib/data';
import { GlobalMixerConfig } from 'common/types/bundle';

type Props = {
  activeStepId: string;
  stepsConfig: StepParamConfigById;
  globalMixerConfig: GlobalMixerConfig;
  onChange: (value: CreateGlobalConfigInputStepState, checked: boolean) => void;
};

export default function ProtocolGlobalMixerPanelItem({
  activeStepId,
  stepsConfig,
  globalMixerConfig,
  onChange,
}: Props) {
  const [showWarning, setShowWarning] = useState(false);

  const otherStepMembership = useMemo(() => {
    return (
      Object.entries(stepsConfig)
        .map(([stepId, stepConfig]) => {
          const isOtherStep = stepId !== activeStepId;
          const hasEntries = Object.keys(stepConfig.globalMixer).length > 0;
          return isOtherStep && hasEntries ? stepConfig.name : undefined;
        })
        .filter(isDefined)
        .join(', ') || undefined
    );
  }, [activeStepId, stepsConfig]);

  const isDisabled = otherStepMembership !== undefined;

  const enabledFieldNames = useMemo(
    () => Object.values(stepsConfig).flatMap(v => Object.keys(v.globalMixer)),
    [stepsConfig],
  );

  return (
    <PanelItem
      displayName="Global settings"
      checked={enabledFieldNames.length > 0}
      onCheck={() => setShowWarning(prev => !prev)}
      otherStepMembership={otherStepMembership}
      isDisabled={isDisabled}
      warnings={
        isDisabled ? undefined : (
          <Stack spacing={4} paddingBottom={4}>
            {showWarning && (
              <FormHelperText error>
                Global settings can not be exposed as a single input. Please expand the
                settings and select a specific option instead.
              </FormHelperText>
            )}
            <FormHelperText>
              Note that global settings will only affect the protocol if specific devices
              are selected.
            </FormHelperText>
          </Stack>
        )
      }
      contents={
        <Stack spacing={4}>
          {GLOBALMIXERCONFIG.map(option => {
            const fieldName = option.name as keyof GlobalMixerConfig;
            const displayName = option.configuration!.displayName;
            const optionValue = globalMixerConfig[fieldName];
            const isEnabled = enabledFieldNames.includes(fieldName);
            return (
              <EntrySelector
                key={fieldName}
                entries={{ [displayName]: optionValue }}
                enabledKeys={isEnabled ? [displayName] : undefined}
                showKeys
                valueEditor={option.configuration!.editor}
                isDisabled={isDisabled}
                onToggle={(checked, editor, value) => {
                  const parameter = { fieldName, displayName, editor };
                  onChange({ parameter, value }, checked);
                  setShowWarning(false);
                }}
              />
            );
          })}
        </Stack>
      }
    />
  );
}
